import { App, Pagination, Spin } from 'antd';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getWorldCupHistoryPredictionsAccount } from 'service/base';
import { AUTH_CHANGED_EVENT } from 'utils/common/authEvents';
import { LocalStorageKey } from 'utils/common/const';
import 'styles/global.css';

const PAGE_SIZE = 10;

const BLUE_BAR = '#0053DF';

function resolveAccount() {
  if (!localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)) {
    return '';
  }
  return (
    localStorage.getItem('saved_account') ||
    localStorage.getItem('userAccount') ||
    ''
  )
    .trim()
    .toLowerCase();
}

function normalizeRows(res) {
  const payload = res?.data !== undefined ? res.data : res;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.responseData)) return payload.responseData;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function cellDuDoan(r) {
  const v =
    r?.predictionTitle ??
    r?.predictionTypeLabel ??
    r?.categoryName ??
    r?.typeLabel ??
    r?.type ??
    r?.category ??
    r?.predictionType ??
    r?.section ??
    r?.kind;
  if (v != null && String(v).trim() !== '') return String(v);
  return '—';
}

function cellBanDuDoan(r) {
  const v =
    r?.yourPrediction ??
    r?.userChoice ??
    r?.pick ??
    r?.choice ??
    r?.name ??
    r?.teamName ??
    r?.playerName ??
    r?.title ??
    r?.value;
  if (v != null && String(v).trim() !== '') return String(v);
  if (r?.homeName && r?.awayName) return `${r.homeName} - ${r.awayName}`;
  return '—';
}

function cellKetQua(r) {
  const result = Number(r?.result);
  if (!Number.isFinite(result) || result <= 0) return '-';
  return String(r.result);
}

function cellThoiGian(r) {
  const t = r?.createdAt ?? r?.updatedAt ?? r?.created_at;
  if (!t) return '—';
  return dayjs(t).format('DD/MM/YYYY HH:mm');
}

export function WorldCupHistoryResultsPanel({ className = '' }) {
  const { notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasAccount, setHasAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const acc = resolveAccount();
    setHasAccount(Boolean(acc));

    if (!acc) {
      setRows([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    (async () => {
      try {
        const res = await getWorldCupHistoryPredictionsAccount({
          account: acc,
          site: SITE
        });
        if (cancelled) return;
        setRows(normalizeRows(res));
      } catch {
        if (cancelled) return;
        setRows([]);
        notification.error({
          message: 'Lỗi',
          description: 'Không tải được lịch sử World Cup. Vui lòng thử lại.'
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Chỉ gọi API một lần khi vào tab (mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const run = () => {
      const acc = resolveAccount();
      setHasAccount(Boolean(acc));
      if (!acc) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      (async () => {
        try {
          const res = await getWorldCupHistoryPredictionsAccount({
            account: acc,
            site: SITE
          });
          setRows(normalizeRows(res));
        } catch {
          setRows([]);
          notification.error({
            message: 'Lỗi',
            description: 'Không tải được lịch sử World Cup. Vui lòng thử lại.'
          });
        } finally {
          setLoading(false);
        }
      })();
    };
    window.addEventListener(AUTH_CHANGED_EVENT, run);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  const emptyMessage = !hasAccount
    ? 'Vui lòng đăng nhập hoặc nhập tài khoản dự đoán trên trang chủ để xem lịch sử World Cup.'
    : 'Chưa có dữ liệu lịch sử.';

  return (
    <div className={className}>
      <div className="rounded-t-xl overflow-hidden border border-[#D9E2EF] shadow-sm">
        <div
          className="grid grid-cols-4 gap-0 text-white font-bold text-[10px] sm:text-xs md:text-sm py-2.5 md:py-3 px-1 md:px-3 text-center items-center"
          style={{
            background: BLUE_BAR,
            boxShadow: 'inset 0 -3px 0 rgba(0,0,0,0.12)'
          }}
        >
          <span className="px-0.5 leading-tight">Thời gian</span>
          <span className="px-0.5 leading-tight">Dự đoán</span>
          <span className="px-0.5 leading-tight">Bạn dự đoán</span>
          <span className="px-0.5 leading-tight">Kết quả</span>
        </div>

        <div
          className="min-h-[220px] md:min-h-[260px] relative"
          style={{
            background: 'linear-gradient(180deg, #FFFCF7 0%, #F6F2EA 100%)'
          }}
        >
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/40">
              <Spin />
            </div>
          ) : rows.length === 0 ? (
            <p className="py-14 md:py-20 text-center text-[#7A8494] text-sm md:text-base px-4">{emptyMessage}</p>
          ) : (
            <ul className="divide-y divide-[#E8E4DC]">
              {pagedRows.map((r, i) => (
                <li
                  key={String(r._id ?? r.id ?? `${cellThoiGian(r)}-${i}`)}
                  className="grid grid-cols-4 gap-0 text-[10px] sm:text-xs md:text-sm py-2.5 md:py-3 px-1 md:px-3 text-[#2D3748] items-center"
                >
                  <span className="text-center break-words px-0.5">{cellThoiGian(r)}</span>
                  <span className="text-center break-words px-0.5 font-medium">{cellDuDoan(r)}</span>
                  <span className="text-center break-words px-0.5 text-[#0053DF] font-semibold">{cellBanDuDoan(r)}</span>
                  <span className="text-center break-words px-0.5">{cellKetQua(r)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {!loading && rows.length > PAGE_SIZE ? (
        <div className="flex justify-end mt-3">
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={rows.length}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      ) : null}
    </div>
  );
}
